import { useMemo } from 'react'

import {
  DayOfWeek,
  RecurringEventFrequency,
} from '@tribeplatform/gql-client/types'
import { Badge } from '@tribeplatform/react-ui-kit/Badge'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'

import { useI18n } from '../../i18n/index.js'
import { getDayOfWeekText } from '../hooks/useDaysOfWeekItems.js'

interface Props {
  frequency?: RecurringEventFrequency
  repeatsOn?: DayOfWeek[]
}

export const RecurrenceInformation = ({ frequency, repeatsOn }: Props) => {
  const { $t, formatList } = useI18n()

  const text = useMemo(() => {
    switch (frequency) {
      case RecurringEventFrequency.daily:
        return $t({
          id: 'Event.Details.RecurringBadge.Daily',
          defaultMessage: 'Repeats daily',
        })
      case RecurringEventFrequency.weekly:
        return $t(
          {
            id: 'Event.Details.RecurringBadge.Weekly',
            defaultMessage:
              'Repeats weekly {daysLength, plural, one {on {days}} other {}}',
          },
          {
            daysLength: repeatsOn.length,
            days: formatList(
              repeatsOn.map(dayOfWeek => getDayOfWeekText(dayOfWeek)),
            ),
          },
        )
      case RecurringEventFrequency.monthly:
        return $t({
          id: 'Event.Details.RecurringBadge.Monthly',
          defaultMessage: 'Repeats monthly',
        })
      case RecurringEventFrequency.yearly:
        return $t({
          id: 'Event.Details.RecurringBadge.Yearly',
          defaultMessage: 'Repeats yearly',
        })
      case RecurringEventFrequency.once:
        return null
      default: {
        const _exhaustiveCheck: never = frequency
        return _exhaustiveCheck
      }
    }
  }, [frequency, $t, formatList, repeatsOn])

  if (!frequency) {
    return null
  }

  return (
    <Badge
      variant="decorative7"
      color="primary"
      className="flex items-center gap-x-2 w-fit"
    >
      <SvgIcon name="refresh" size="sm" />
      {text}
    </Badge>
  )
}
